import { useContext, useEffect, useMemo, useState } from "react";
import { bool, number, node, arrayOf, object } from "prop-types";
import { MAX_WORK_TYPE_LENGTH, zip, unzip } from "@repo/utils";
import { areaAutoSearch, autoBreakdownSearch, cutSheetArea } from "@utils";
import {
  createXlsImportConfigQuery,
  getCompanySettingsQuery,
  getXlsImportConfigsQuery,
  removeXlsImportConfigQuery,
  setCompanySettingsQuery,
  updateXlsImportConfigQuery
} from "@query";
import { COMPANY_SETTINGS } from "@client";
import { XlsImporterSettingsContext } from "./XlsImporterSettingsContext";

export const useXlsImporterSettings = () => {
  const context = useContext(XlsImporterSettingsContext);
  if (context === null) {
    throw new Error("EstimateTableSettings cannot be null, please add a context provider");
  }
  return context;
};

export const DEFAULT_STEPS = [
  { id: 1, completed: false, locked: false },
  { id: 2, completed: false, locked: true },
  { id: 3, completed: false, locked: true },
  { id: 4, completed: false, locked: true },
  { id: 5, completed: false, locked: true },
];

export const XlsImporterSettingsProvider = ({
  children,
  workTypes,
  defaultStructure,
  defaultWorkTypes,
  defaultUseMinMax = false,
  defaultApplyReverseBuffers = false,
  defaultTimeModifier = 1,
}) => {
  
  const [ importConfigurations, setImportConfigurations ] = useState([]);
  const [ defaultConfiguration, setDefaultConfiguration ] = useState(null);
  const [ usedConfiguration, setUsedConfiguration ] = useState(null);

  const [ useFastMode, setUseFastMode ] = useState(true);
  const [ xlsData, setXlsData ] = useState([]);
  const [ xlsFileName, setXlsFileName ] = useState();
  const [ importModules, setImportModules] = useState([]);
  
  const [ fastModeMapping, setFastModeMapping] = useState([null,null,null,null]);
  
  const [ customWorkTypes, setCustomWorkTypes] = useState([]);
  const [ importWorkTypes, setImportWorkTypes] = useState(
    defaultWorkTypes?.map((s) => workTypes.find((t) => s.id === t.id)) || []
  );
  const [ descColumn, setImportDescColumn] = useState(null);
  const [ additionalTextColumns, setAdditionalTextColumns ] = useState([]);
  const [ steps, setSteps] = useState(structuredClone(DEFAULT_STEPS));
  const [ activeStep, setActiveStep] = useState(0);
  const [ activeSheet, setActiveSheet] = useState(-1);
  const [ breakdownXlsData, setBreakdownXlsData] = useState([]);
  const [ breakdownSelection, setBreakdownSelection] = useState([]);
  const [ titleColumnsLength, setTitleColumnsLength] = useState(1);
  const [ importTimeModifier, setImportTimeModifier] = useState(defaultTimeModifier);
  const [ useMinMax, setMinMax] = useState(defaultUseMinMax);
  const [ applyReverseBuffers, setApplyReverseBuffers ] = useState(defaultApplyReverseBuffers);
  const [ importedStructure, setImportedStructure] = useState(null);
  
  const currentSheet = useMemo(() => (
    xlsData?.length ? xlsData[activeSheet] : undefined
  ), [xlsData, activeSheet]);

  const currentBreakdownData = useMemo(() => (
    breakdownXlsData[activeSheet]
  ), [breakdownXlsData, activeSheet]);
  
  useEffect(() => {
    if(steps[3].locked || activeStep < 3 )
      return;
    
    let isSet;
    if(useFastMode)
      isSet = Boolean(fastModeMapping.filter(e => typeof e === "number").length);
    else
      isSet = Boolean(importModules.length);
    
    if(steps[3].completed === isSet && steps[4].locked !== isSet)
      return;
    
    completeStep(3, isSet);
    unlockStep(4, isSet);
  }, [steps, useFastMode, fastModeMapping, importModules]);
  
  useEffect(() => {
    (async () => {
      const c = await getXlsImportConfigsQuery();
      const d = await getCompanySettingsQuery(COMPANY_SETTINGS.XLS_CONFIG);
      setImportConfigurations(c);
      
      if(typeof d === "number") {
        const selectedConfigData = c.find(sC => sC.id === d);
        if(!selectedConfigData)
          return;
        
        setDefaultConfiguration(d);
        selectImportConfiguration(selectedConfigData);
      }
    })();
  }, []);

  const usedWorkTypes = importWorkTypes.filter(wT => wT.id);

  const initXlsData = (data, file) => {
    // resetXlsImportToDefaults();
    setXlsData(data);
    completeStep(0);
    unlockStep(1);
    setActiveStep(1);
    setBreakdownXlsData(new Array(data.length));
    setXlsFileName(file);
  }
  
  const handleActiveStepChange = (s) => {
    setActiveStep(s)
    /** @notice: imho, should be used only with "apropo import template" */
    if (s === 2) autoAreaCalculations();
    if (s === 3) parseBreakdownSheet();
  }
  
  const updateActiveSheet = (index) => {
    setActiveSheet(index);
    completeStep(1);
    unlockStep(2);
    completeStep(2, false);
    unlockStep(3, false);
    updateBreakdownSelection(
      breakdownXlsData.length
        ? breakdownXlsData[index]?.selection || []
        : []
    );
  }
  
  const updateBreakdownSelection = (selection) => {
    setBreakdownSelection(selection);
    completeStep(2, !!selection.length);
    unlockStep(3, !!selection.length);
    
    if(selection.length)
      adjustSelectedColumns(selection, usedConfiguration ? JSON.parse(unzip(usedConfiguration.config)) : null);
  }
  
  const adjustSelectedColumns = (selection, config) => {
    const columns = selection[2] - selection[0];
    const rows = selection[3] - selection[1];
    
    let newWorkTypes = importWorkTypes;
    
    if(config) {
      setFastModeMapping(config.levels.map(l => typeof l === "number" && l <= columns ? l : null));
      setImportDescColumn(typeof config.description === "number" && config.description <= columns ? config.description : null);
      setAdditionalTextColumns((config.additionalTextColumns || []).map((textColumn, index) => ({
        name: textColumn.name || `Text column ${index + 2}`,
        column: typeof textColumn.column === "number" && textColumn.column <= columns ? textColumn.column : null,
      })));
      newWorkTypes = config.workTypes.map(wT => ({
        ...wT,
        ...(workTypes.find(w => w.id === wT.id) || {}),
      }));
    } else {
      setFastModeMapping(fastModeMapping.map((l, i) => i <= columns ? i : null));
      setImportDescColumn(columns >= fastModeMapping.length ? fastModeMapping.length : null);
      setAdditionalTextColumns([]);
      setImportModules(importModules?.filter(({ x,y }) => x <= columns && y <= rows) || []);
    }

    setImportWorkTypes(newWorkTypes.map(wT => ({
      ...wT,
      column: typeof wT.column === "number" && wT.column <= columns ? wT.column : null,
      columnMax: typeof wT.columnMax === "number" && wT.columnMax <= columns ? wT.columnMax : null,
    })));
  }
  
  const completeStep = (index, complete = true) => {
    setSteps(s => {
      const currentSteps = [...s];
      currentSteps[index].completed = complete;
      return currentSteps;
    });
  }
  
  const unlockStep = (index, unlock = true) => {
    setSteps(s => {
      const currentSteps = [...s];
      currentSteps[index].locked = !unlock;
      return currentSteps;
    });
  }
  
  const autoAreaCalculations = () => {
    if (!currentBreakdownData?.selection?.length) {
      const {
        breakdownSelection,
        // titleColumnsLength
      } = areaAutoSearch(xlsData[activeSheet]);
      
      const u = [...breakdownXlsData];
      u[activeSheet] = { selection: breakdownSelection };
      setBreakdownXlsData(u);
      updateBreakdownSelection(breakdownSelection);
      // setTitleColumnsLength(titleColumnsLength);
    }
  };

  const parseBreakdownSheet = () => {
    const d = breakdownXlsData[activeSheet];
    let modules = [];
    if (!d.sheet || d.selection !== breakdownSelection) {
      breakdownXlsData[activeSheet].selection = breakdownSelection;
      const area = cutSheetArea(currentSheet, breakdownSelection);
      breakdownXlsData[activeSheet].sheet = area;
      
      modules = autoBreakdownSearch(
        area,
        titleColumnsLength,
        workTypes,
        MAX_WORK_TYPE_LENGTH
      ).modules;
      
      setImportModules(modules);
      // this.setImportWorkTypes(workTypes);
    }
  }
  
  const changeImportModules = (row, col, type, clear = false) => {
    setImportModules((m) => {
      let u = [...m];
      const f = u.find((s) => s.x === col && s.y === row);
      if (f) {
        if (clear)
          u = u.filter((s) => s.x !== col || s.y !== row);
        else f.type = type;
      } else if (!clear)
        u.push({
          name: currentBreakdownData.sheet.data[row][col],
          x: col,
          y: row,
          type,
        })
      
      setTitleColumnsLength(Math.max(...u.map(fm => (fm.x || 0) + 1)));
      return u;
    });
  }
  
  const changeImportColumns = (index, col) => {
    setFastModeMapping((m) => {
      let u = [...m];
      u[index] = col;
      
      setTitleColumnsLength(Math.max(...u.map(fm => (fm || 0) + 1)));
      return u;
    });
  }

  const addCustomWorkType = (workType) => {
    setCustomWorkTypes(list => {
      list.push(workType);
      return list;
    })
  }

  const removeCustomWorkType = (id) => {
    setCustomWorkTypes(list => list.filter((w) => w.id !== id))
  }
  
  const updateImportWorkType = (index, workTypeData) => {
    setImportWorkTypes(i => {
      const u = [...i];
      u[index] = { ...u[index], ...workTypeData };
      return u;
    });
  }
  
  const resetXlsImportToDefaults = () => {
    setSteps(structuredClone(DEFAULT_STEPS));
    setXlsData([]);
    setActiveStep(0);
    setActiveSheet(-1);
    setBreakdownXlsData([]);
    setBreakdownSelection([]);
    setImportModules([]);
    setTitleColumnsLength(1);
    setMinMax(false);
    setImportTimeModifier(1);
    setCustomWorkTypes([]);
    setAdditionalTextColumns([]);

    setImportWorkTypes([]);
    setImportedStructure(null);
    setXlsFileName(null);
  }
  
  const getSerializedConfig = () => JSON.stringify({
    levels: fastModeMapping,
    description: descColumn,
    additionalTextColumns,
    workTypes: importWorkTypes,
    useMinMax,
    importTimeModifier,
    applyReverseBuffers,
  });
  
  const createImportConfiguration = async (name) => {
    const res = await createXlsImportConfigQuery(name, zip(getSerializedConfig()));
    setImportConfigurations([...importConfigurations, res]);
    selectImportConfiguration(res);
  }
  
  const updateImportConfigurationName = async (id, name) => {
    await updateXlsImportConfigQuery(id, name);
    setImportConfigurations((list) => {
      const newList = { ...list };
      const el = newList.find((c) => c.id === id);
      if(el)
        el.name = name;
      return newList;
    })
  }
  
  const updateImportConfiguration = async (nonSelectedConfigId=null) => {
    if(!usedConfiguration?.id && !nonSelectedConfigId)
      return;
    
    const id = nonSelectedConfigId ||  usedConfiguration.id;
    
    const config = zip(getSerializedConfig());
    await updateXlsImportConfigQuery(id, undefined, config);

    const newList = [ ...importConfigurations ];
    const selectedConfigData = newList.find((c) => c.id === id);
    if(selectedConfigData)
      selectedConfigData.config = config;
    setImportConfigurations(newList);
    
    if(typeof nonSelectedConfigId === "number")
      selectImportConfiguration(selectedConfigData);
  }
  
  const selectImportConfiguration = (selectedConfigData) => {
    let config;
    try {
      config = selectedConfigData?.config;
      if(!config)
        return;
      
      const unpacked = JSON.parse(unzip(config));
      
      setMinMax(unpacked.useMinMax || false);
      setApplyReverseBuffers(unpacked.applyReverseBuffers || false)
      setImportTimeModifier(
        typeof unpacked.importTimeModifier === "number"
        ? unpacked.importTimeModifier
        : defaultTimeModifier
      );
      
      adjustSelectedColumns(breakdownSelection, unpacked);
      
      setUsedConfiguration(selectedConfigData);
    } catch (e) {
      console.error("Could not unpack saved configuration: ", config);
    }
  }
  
  const setDefaultImportConfiguration = async (id) => {
    await setCompanySettingsQuery(COMPANY_SETTINGS.XLS_CONFIG, id);
    setDefaultConfiguration(id);
  }
  
  const removeImportConfiguration = async (id) => {
    await removeXlsImportConfigQuery(id);
    setImportConfigurations((list) => list.filter((c) => c.id !== id));
    if(usedConfiguration?.id === id)
      setUsedConfiguration(null);
  }
  
  const handleSetImportedStructure = (structure) => {
    setImportedStructure(structure);
    completeStep(4, Boolean(structure));
  }
  
  const handleSetUseFastMode = (newValue) => {
    setUseFastMode(newValue);
  }
  
  const value = {
    workTypes,
    customWorkTypes,
    defaultStructure,
    hasDefaultStructure: Boolean(defaultStructure),
    defaultConfiguration,
    usedConfiguration,
    importConfigurations,
    
    initXlsData,
    useFastMode,
    handleSetUseFastMode,
    
    fastModeMapping,
    
    xlsData,
    xlsFileName,
    setXlsFileName,
    importModules,
    usedWorkTypes,
    importWorkTypes,
    setImportWorkTypes,
    descColumn,
    setImportDescColumn,
    additionalTextColumns,
    setAdditionalTextColumns,
    steps,
    activeStep,
    setActiveStep,
    activeSheet,
    setActiveSheet,
    breakdownXlsData,
    breakdownSelection,
    titleColumnsLength,
    importTimeModifier,
    setImportTimeModifier,
    useMinMax,
    setMinMax,
    applyReverseBuffers,
    setApplyReverseBuffers,
    currentSheet,
    currentBreakdownData,
    importedStructure,
    handleSetImportedStructure,
    handleActiveStepChange,
    updateActiveSheet,
    updateBreakdownSelection,
    completeStep,
    unlockStep,
    autoAreaCalculations,
    parseBreakdownSheet,
    changeImportColumns,
    changeImportModules,
    updateImportWorkType,
    resetXlsImportToDefaults,
    addCustomWorkType,
    removeCustomWorkType,
    
    createImportConfiguration,
    updateImportConfigurationName,
    updateImportConfiguration,
    removeImportConfiguration,
    selectImportConfiguration,
    setDefaultImportConfiguration,
  };
  
  return <XlsImporterSettingsContext.Provider value={value}>
    {children}
  </XlsImporterSettingsContext.Provider>
}

XlsImporterSettingsProvider.propTypes = {
  children: node.isRequired,
  workTypes: arrayOf(object).isRequired,
  defaultStructure: object,
  defaultWorkTypes: arrayOf(object),
  defaultUseMinMax: bool,
  defaultApplyReverseBuffers: bool,
  defaultTimeModifier: number
}
